import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, Package, ArrowRight } from "lucide-react";
import api from "@/lib/zafaraApi";
import { useShop } from "@/context/ShopContext";

const formatXOF = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(n) || 0) + " F";

const STATUS_LABELS = {
  pending_payment: { label: "En attente paiement", color: "#F5A800" },
  pending: { label: "En attente", color: "#F5A800" },
  confirmed: { label: "Confirmée", color: "#0E7A3B" },
  assigned: { label: "Livreur attribué", color: "#0E7A3B" },
  picked_up: { label: "Colis récupéré", color: "#0E7A3B" },
  in_delivery: { label: "En livraison", color: "#0E7A3B" },
  delivered: { label: "Livrée", color: "#0E7A3B" },
  cancelled: { label: "Annulée", color: "#E8112D" },
};

export default function MesCommandes() {
  const { user } = useShop();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.ordersMine();
        setOrders(r?.orders || r || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [user]);

  if (!user) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-20 text-center">
        <Package className="h-10 w-10 mx-auto text-[#16241B]/30" />
        <h1 className="mt-6 font-display text-3xl text-[#16241B]">Connectez-vous pour voir vos commandes</h1>
        <Link to="/compte/connexion?next=/mes-commandes" className="mt-6 inline-flex rounded-full bg-[#0E7A3B] text-white px-6 py-3 font-semibold hover:bg-[#0d6a34] transition-colors">
          Se connecter
        </Link>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-4xl px-5 sm:px-8 py-10 md:py-16">
      <Helmet>
        <title>Mes commandes — Zafara Bénin</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <h1 className="font-display text-4xl md:text-5xl text-[#16241B] tracking-tighter">
        Mes <span className="text-[#0E7A3B]">commandes</span>
      </h1>
      <p className="mt-2 text-[#16241B]/70">Historique et suivi en direct depuis votre compte Zafara.</p>

      {loading ? (
        <div className="py-16 flex justify-center text-[#16241B]/60"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-dashed border-[#EDE7D9] p-12 text-center">
          <Package className="h-10 w-10 mx-auto text-[#16241B]/30" />
          <p className="mt-4 text-[#16241B]/70">Aucune commande pour le moment.</p>
          <Link to="/catalogue" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3 font-semibold hover:bg-[#0E7A3B] hover:text-white transition-colors">
            Explorer le catalogue <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <ul data-testid="orders-list" className="mt-8 space-y-3">
          {orders.map((o) => {
            const st = STATUS_LABELS[o.status] || { label: o.status || "?", color: "#16241B" };
            const code = (o.delivery_code || o.tracking_code || o.id || "").toString().slice(0, 12).toUpperCase();
            return (
              <li key={o.id} data-testid={`order-row-${o.id}`} className="rounded-2xl border border-[#EDE7D9] bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/50">Code de suivi</p>
                    <p className="font-display font-bold text-lg text-[#16241B]">#{code}</p>
                    <p className="mt-1 text-xs text-[#16241B]/60">
                      {o.created_at ? new Date(o.created_at).toLocaleString("fr-FR") : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className="inline-flex rounded-full px-3 py-1 text-[10px] font-utility uppercase tracking-widest"
                      style={{ backgroundColor: `${st.color}15`, color: st.color }}
                    >
                      {st.label}
                    </span>
                    <p className="mt-2 font-display font-bold text-[#0E7A3B]">{formatXOF(o.total || 0)}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Link
                    to={`/suivi/${o.id}`}
                    className="inline-flex items-center gap-2 text-sm text-[#0E7A3B] hover:underline font-medium"
                  >
                    Suivre la livraison <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
